/**
 * Pure helpers for the Free Trials admin page.
 *
 * Nothing here decides anything the server also decides. The backend owns
 * validation, status, and the day boundary; these functions only describe what
 * the administrator has typed so far, so the form can show the length of the
 * trial before it is saved, and pre-fill the Subscriber form on a conversion.
 *
 * Kept out of the component so they can be tested without rendering.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a `YYYY-MM-DD` value as UTC midnight.
 *
 * Deliberately not `new Date(value)` for a bare date string and never
 * `new Date(y, m, d)`: both introduce a local-time reading, which shifts the
 * day for anyone not on UTC and would make the preview disagree with the date
 * the server stores.
 *
 * @param {string} value
 * @returns {Date|null} null when the value is not a usable calendar date
 */
export function parseDateOnly(value) {
  if (typeof value !== "string" || !DATE_PATTERN.test(value)) {
    return null;
  }
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Describes the period the two date fields currently define.
 *
 * Returns `null` while either field is empty, so the form says nothing rather
 * than guessing. An invalid range comes back with `valid: false` and the
 * reason, which the page shows and uses to keep the submit button disabled —
 * the server rejects the same range, this just says so sooner.
 *
 * @param {string} startDate `YYYY-MM-DD`
 * @param {string} expiryDate `YYYY-MM-DD`
 * @returns {{valid: boolean, days: number|null, message: string}|null}
 */
export function describeTrialPeriod(startDate, expiryDate) {
  if (!startDate || !expiryDate) {
    return null;
  }

  const start = parseDateOnly(startDate);
  const expiry = parseDateOnly(expiryDate);

  if (!start || !expiry) {
    return { valid: false, days: null, message: "Enter both dates as YYYY-MM-DD." };
  }

  if (expiry.getTime() <= start.getTime()) {
    return {
      valid: false,
      days: null,
      message: "The expiry date must be after the start date.",
    };
  }

  // Inclusive of both endpoints, which is how the expiry day itself is
  // treated: a trial is still active for the whole of its expiry date.
  const days = Math.round((expiry.getTime() - start.getTime()) / DAY_MS) + 1;

  return {
    valid: true,
    days,
    message: `${days} ${days === 1 ? "day" : "days"} — ends ${expiryDate}`,
  };
}

/**
 * The Subscriber form's starting values for a conversion.
 *
 * Only fields a free trial genuinely holds are carried across. Everything the
 * subscriber system requires and the trial cannot know — the referrer, the
 * amount, the subscription dates — is deliberately left empty for the
 * administrator to fill in, rather than being invented here.
 *
 * A trial's `notes` are not copied: they are notes about a prospect, and
 * carrying them onto a customer record silently would put text the admin wrote
 * in one context into another.
 *
 * @param {{id: string, name: string, phone_number?: string, email?: string}} freeTrial
 * @returns {{free_trial_id: string, full_name: string, phone_number: string, email: string}}
 */
export function buildSubscriberPrefill(freeTrial) {
  if (!freeTrial || !freeTrial.id) {
    return null;
  }
  return {
    free_trial_id: freeTrial.id,
    full_name: freeTrial.name || "",
    phone_number: freeTrial.phone_number || "",
    // Subscribers have no email field. Carried so the page can show which
    // trial is being converted, not to be submitted.
    email: freeTrial.email || "",
  };
}

/** Badge tone for a status, matching the admin badge classes. */
export function statusTone(status) {
  switch (status) {
    case "ACTIVE":
      return "on";
    case "CONVERTED":
      return "won";
    default:
      return "off";
  }
}

/** Human-readable status label. */
export function statusLabel(status) {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "EXPIRED":
      return "Expired";
    case "CONVERTED":
      return "Converted";
    default:
      return status || "—";
  }
}
